import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Button, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, CircularProgress } from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { spAPI } from '../../services/api';
import DataTable, { StatusChip } from '../../components/common/DataTable';
import { toast } from 'react-toastify';

const fmt = n => new Intl.NumberFormat('en-US',{style:'currency',currency:'USD'}).format(n||0);

const SupplierForm = ({ open, onClose, onSaved }) => {
  const { register, handleSubmit, reset } = useForm();
  const [loading, setLoading] = useState(false);
  useEffect(() => { reset({ payment_terms: 30, currency: 'USD' }); }, [open, reset]);
  const onSubmit = async (data) => {
    try { setLoading(true); await spAPI.createSupplier(data); toast.success('Supplier added'); onSaved(); onClose(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>Add Parts Supplier</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Supplier Name *" {...register('name', { required: true })} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Contact Person" {...register('contact_person')} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Phone" {...register('phone')} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Email" {...register('email')} /></Grid>
            <Grid item xs={8}><TextField fullWidth label="Address" {...register('address')} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Country" {...register('country')} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Payment Terms (days)" type="number" {...register('payment_terms', { valueAsNumber: true })} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Currency" {...register('currency')} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Notes" multiline rows={2} {...register('notes')} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading}
            sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
            {loading ? <CircularProgress size={20} /> : 'Add Supplier'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const SparePartsSuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]); const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0); const [page, setPage] = useState(0); const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState(''); const [formOpen, setFormOpen] = useState(false);

  const fetchSuppliers = useCallback(async () => {
    try { setLoading(true);
      const { data } = await spAPI.getSuppliers({ page: page+1, limit: rowsPerPage, search: search||undefined });
      setSuppliers(data.data||[]); setTotal(data.pagination?.total||0);
    } catch { toast.error('Failed'); } finally { setLoading(false); }
  }, [page, rowsPerPage, search]);
  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const columns = [
    { key: 'supplier_code', label: 'Code', render: v => <Typography fontFamily="monospace" fontWeight={600} fontSize="0.8rem">{v}</Typography> },
    { key: 'name', label: 'Supplier', render: v => <Typography fontWeight={600}>{v}</Typography> },
    { key: 'contact_person', label: 'Contact' }, { key: 'phone', label: 'Phone' },
    { key: 'country', label: 'Country' }, { key: 'payment_terms', label: 'Terms', render: v => `${v} days` },
    { key: 'currency', label: 'Currency' },
    { key: 'is_active', label: 'Status', render: v => <StatusChip status={v ? 'active' : 'inactive'} /> },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={800}>Parts Suppliers</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)}
          sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>Add Supplier</Button>
      </Box>
      <Box sx={{ mb: 3 }}>
        <TextField placeholder="Search suppliers..." size="small" sx={{ minWidth: 300 }}
          value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
      </Box>
      <DataTable columns={columns} rows={suppliers} loading={loading} total={total} page={page} rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }} />
      <SupplierForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={fetchSuppliers} />
    </Box>
  );
};

export default SparePartsSuppliersPage;
